import { useQuery } from '@tanstack/react-query';
import { useOrgStore } from '@/stores/org-store';
import { getFilestoreTree } from '@/services/filestore/get-tree';
import { getFilestoreHistory } from '@/services/filestore/get-history';
import type {
  FilestoreCommit,
  FilestoreTreeEntry,
  FilestoreTreeResponse,
} from '@/types/filestore';
import { FILESTORE_QUERY_KEY } from './use-filestore-repos';

export const useFilestoreTree = (repo: string | undefined, dirPath = '') => {
  const { currentOrg } = useOrgStore();
  const orgId = currentOrg?.orgid;

  return useQuery<FilestoreTreeResponse, Error>({
    queryKey: [FILESTORE_QUERY_KEY, orgId, 'tree', repo, dirPath],
    queryFn: () => getFilestoreTree(orgId!, repo!, dirPath),
    enabled: !!orgId && !!repo,
    staleTime: 30_000,
    retry: 1,
  });
};

/**
 * The tree endpoint carries no timestamps (git trees don't), so the Updated
 * column comes from a limit-1 history call per entry. Capped so a huge folder
 * doesn't fan out into hundreds of requests; rows beyond the cap show "—".
 */
const LAST_COMMIT_LOOKUP_CAP = 30;

export const useFilestoreLastCommits = (
  repo: string | undefined,
  dirPath: string,
  entries: FilestoreTreeEntry[] | undefined,
) => {
  const { currentOrg } = useOrgStore();
  const orgId = currentOrg?.orgid;
  const paths = (entries ?? [])
    .slice(0, LAST_COMMIT_LOOKUP_CAP)
    .map((entry) => entry.path);

  return useQuery<Record<string, FilestoreCommit>, Error>({
    queryKey: [
      FILESTORE_QUERY_KEY,
      orgId,
      'last-commits',
      repo,
      dirPath,
      paths,
    ],
    queryFn: async () => {
      const results = await Promise.allSettled(
        paths.map((entryPath) =>
          getFilestoreHistory(orgId!, repo!, entryPath, { limit: 1 }),
        ),
      );
      const byPath: Record<string, FilestoreCommit> = {};
      results.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value.commits[0]) {
          byPath[paths[index]] = result.value.commits[0];
        }
      });
      return byPath;
    },
    enabled: !!orgId && !!repo && paths.length > 0,
    staleTime: 30_000,
    retry: 0,
  });
};
